import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, User, Mail, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ManagerAcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/auth/manager/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Invite not found or expired"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/manager/accept-invite", {
        invite_token: token, password: pw,
      });
      localStorage.setItem("pr_session_token", data.session_token);
      setUser(data.user);
      toast.success("Welcome to the Plenty Rydez Back Office");
      navigate("/backoffice");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invite");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="manager-accept-page" className="min-h-[70vh] flex items-center justify-center px-5 py-16">
      <div className="w-full max-w-md">
        <div className="glass rounded-3xl p-8 space-y-6">
          <div className="text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-gold/15 flex items-center justify-center mb-3">
              <ShieldCheck size={22} className="text-gold" />
            </div>
            <div className="label-caps text-electric text-xs">Back Office invite</div>
            <h1 className="font-serif text-3xl mt-1">Set up your manager account</h1>
          </div>

          {error && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3">
              {error}
            </div>
          )}

          {!invite && !error && (
            <div className="text-center text-white/60 text-sm inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading invite…
            </div>
          )}

          {invite && (
            <form onSubmit={submit} className="space-y-4">
              <div className="rounded-xl border border-white/10 bg-white/[.03] p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-white/70">
                  <User size={12} className="text-electric" />
                  <span>{invite.name}</span>
                </div>
                <div className="flex items-center gap-2 text-white/70">
                  <Mail size={12} className="text-electric" />
                  <span>{invite.email}</span>
                </div>
                {invite.invited_by && (
                  <div className="text-xs text-white/40">
                    Invited by {invite.invited_by}
                  </div>
                )}
              </div>

              <label className="block">
                <div className="label-caps text-white/60 mb-2 flex items-center gap-1.5">
                  <KeyRound size={12} className="text-electric" />Choose a password
                </div>
                <input
                  data-testid="manager-pw"
                  type="password" value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  minLength={8} required
                  className="input"
                  autoComplete="new-password"
                />
              </label>
              <label className="block">
                <div className="label-caps text-white/60 mb-2 flex items-center gap-1.5">
                  <KeyRound size={12} className="text-electric" />Confirm password
                </div>
                <input
                  data-testid="manager-pw2"
                  type="password" value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  minLength={8} required
                  className="input"
                  autoComplete="new-password"
                />
              </label>

              <button
                type="submit" disabled={busy}
                data-testid="manager-accept-submit"
                className="btn-gold w-full py-3 rounded-full text-sm disabled:opacity-50"
              >
                {busy ? "Setting up…" : "Create manager account"}
              </button>
              <p className="text-[11px] text-white/40 text-center">
                By continuing you agree to safeguard your login credentials and act
                on behalf of Plenty Rydez management.
              </p>
            </form>
          )}
        </div>
      </div>
      <style>{`
        .input {
          background: #121214; border: 1px solid rgba(255,255,255,0.08);
          color: #fff; border-radius: 12px; padding: 12px 14px;
          width: 100%; font-size: 14px;
          transition: border-color 0.2s;
        }
        .input:focus { outline: none; border-color: #F9C97C; box-shadow: 0 0 0 3px rgba(249,201,124,0.15); }
      `}</style>
    </section>
  );
}
